import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoLockup } from "./Logo";

const links = [
  { label: "About", id: "about" },
  { label: "Capabilities", id: "services" },
  { label: "Strengths", id: "strengths" },
  { label: "Leadership", id: "leadership" },
  { label: "Contact", id: "contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "border-b border-vg-border bg-white/80 py-3 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent py-5"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 lg:px-10">
        <button onClick={() => go("hero")} data-testid="nav-logo-btn" className="cursor-pointer">
          <LogoLockup className="h-9 w-auto" />
        </button>

        <nav className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="group relative text-sm font-medium text-vg-ink/80 transition-colors duration-300 hover:text-vg-primary"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-vg-primary transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            data-testid="nav-cta-btn"
            className="rounded-full bg-vg-primary px-5 py-2.5 text-sm font-medium text-white transition-transform duration-300 hover:scale-[1.04]"
          >
            Work with us
          </button>
        </nav>

        <button
          className="md:hidden text-vg-primary"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-vg-border bg-white/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="py-3 text-left text-base font-medium text-vg-ink"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
